import React, { useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl, readJsonResponse } from '../apiBase';
import {
    buildAsRepairListSummary,
    normalizeAsRepairFormConfig,
} from '../utils/asRepairFormConfig';
import {
    AS_REPAIR_QTY_CHART_BUILTIN_GROUP_IDS,
    buildDefaultAsRepairQtyChartConfig,
    normalizeAsRepairQtyChartConfig,
} from '../utils/asRepairQtyChartConfig';
import { previewAsRepairQtyChartFieldMatches } from '../utils/asRepairQtyDetailCategories';
import AppleSwitchCheckbox from './AppleSwitchCheckbox';
import './ProductRegistrationSettingsPage.css';
import './AsRepairQtyChartSettingsPage.css';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const BUILTIN_GROUP_IDS = new Set(AS_REPAIR_QTY_CHART_BUILTIN_GROUP_IDS);

const moveItem = (list, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= list.length) {
        return list;
    }
    const next = [...list];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    return next;
};

const FieldKeyPicker = ({ fieldKeys, fields, onChange, disabled, placeholder }) => {
    const labelByKey = useMemo(() => new Map(fields.map((f) => [f.key, f.label])), [fields]);
    return (
        <div className="qty-chart-field-picker">
            {fieldKeys.map((key) => (
                <span
                    key={key}
                    className={`qty-chart-field-chip${labelByKey.has(key) ? '' : ' qty-chart-field-chip--missing'}`}
                >
                    {labelByKey.get(key) || `${key} (삭제된 필드)`}
                    <button
                        type="button"
                        className="qty-chart-field-chip-remove"
                        onClick={() => onChange(fieldKeys.filter((k) => k !== key))}
                        disabled={disabled}
                        aria-label={`${labelByKey.get(key) || key} 연결 해제`}
                    >
                        ×
                    </button>
                </span>
            ))}
            <select
                className="field-input qty-chart-field-select"
                value=""
                onChange={(e) => {
                    const key = e.target.value;
                    if (key) {
                        onChange([...fieldKeys, key]);
                    }
                }}
                disabled={disabled}
            >
                <option value="">{placeholder}</option>
                {fields
                    .filter((f) => !fieldKeys.includes(f.key))
                    .map((f) => (
                        <option key={f.key} value={f.key}>
                            {f.label}{f.inputType === 'auto' ? ' (자동 계산)' : ''}
                        </option>
                    ))}
            </select>
        </div>
    );
};

const AutoMatchHint = ({ explicit, matchedFields, emptyText }) => {
    if (explicit) {
        return null;
    }
    if (!matchedFields || matchedFields.length === 0) {
        return <p className="qty-chart-auto-hint qty-chart-auto-hint--empty">{emptyText}</p>;
    }
    return (
        <p className="qty-chart-auto-hint">
            자동 연결: {matchedFields.map((f) => f.label).join(', ')}
        </p>
    );
};

const AsRepairQtyChartSettingsPage = ({
    currentUserName,
    asRepairFormConfig,
    initialChartConfig,
    onChartConfigSaved,
    showAppToast,
}) => {
    const savedConfig = useMemo(
        () => normalizeAsRepairQtyChartConfig(initialChartConfig),
        [initialChartConfig],
    );
    const [draft, setDraft] = useState(savedConfig);
    const [openGroupIds, setOpenGroupIds] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setDraft(savedConfig);
    }, [savedConfig]);

    const formConfig = useMemo(() => normalizeAsRepairFormConfig(asRepairFormConfig), [asRepairFormConfig]);
    const listSummary = useMemo(() => buildAsRepairListSummary(formConfig), [formConfig]);
    const qtyFields = useMemo(
        () => formConfig.fields.filter((f) => f.inputType === 'quantity' || f.inputType === 'auto'),
        [formConfig],
    );
    const preview = useMemo(() => {
        // 입력 중 빈 이름도 행 순서가 어긋나지 않도록 매칭되지 않는 임시 이름으로 대체
        const previewConfig = {
            groups: draft.groups.map((g) => ({
                ...g,
                title: g.title || g.id,
                categories: g.categories.map((c, j) => ({
                    ...c,
                    title: String(c.title || '').trim() || `\u0000empty${j}`,
                })),
            })),
        };
        return previewAsRepairQtyChartFieldMatches(formConfig, listSummary, previewConfig);
    }, [formConfig, listSummary, draft]);
    const isDirty = useMemo(
        () => JSON.stringify(draft) !== JSON.stringify(savedConfig),
        [draft, savedConfig],
    );

    const updateGroups = (updater) => {
        setDraft((prev) => ({ ...prev, groups: updater(prev.groups) }));
    };

    const updateGroup = (groupIndex, patch) => {
        updateGroups((groups) => groups.map((g, i) => (i === groupIndex ? { ...g, ...patch } : g)));
    };

    const updateCategory = (groupIndex, catIndex, patch) => {
        updateGroups((groups) => groups.map((g, i) => (
            i === groupIndex
                ? { ...g, categories: g.categories.map((c, j) => (j === catIndex ? { ...c, ...patch } : c)) }
                : g
        )));
    };

    const toggleGroupOpen = (groupId) => {
        setOpenGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const addGroup = () => {
        const id = `group_${Date.now().toString(36)}`;
        updateGroups((groups) => [
            ...groups,
            {
                id,
                title: `새 그룹${groups.length + 1}`,
                color: '#64748b',
                visible: true,
                fieldKeys: [],
                categories: [],
            },
        ]);
        setOpenGroupIds((prev) => new Set(prev).add(id));
    };

    const removeGroup = (groupIndex) => {
        const group = draft.groups[groupIndex];
        if (!window.confirm(`「${group.title}」 그룹을 삭제할까요?`)) {
            return;
        }
        updateGroups((groups) => groups.filter((_, i) => i !== groupIndex));
    };

    const addCategory = (groupIndex) => {
        updateGroups((groups) => groups.map((g, i) => (
            i === groupIndex
                ? { ...g, categories: [...g.categories, { title: `항목${g.categories.length + 1}`, fieldKeys: [] }] }
                : g
        )));
    };

    const removeCategory = (groupIndex, catIndex) => {
        updateGroups((groups) => groups.map((g, i) => (
            i === groupIndex
                ? { ...g, categories: g.categories.filter((_, j) => j !== catIndex) }
                : g
        )));
    };

    const moveCategory = (groupIndex, catIndex, direction) => {
        updateGroups((groups) => groups.map((g, i) => (
            i === groupIndex ? { ...g, categories: moveItem(g.categories, catIndex, direction) } : g
        )));
    };

    const handleResetDefaults = () => {
        if (!window.confirm('그래프 설정을 기본값으로 되돌릴까요? (저장해야 반영됩니다)')) {
            return;
        }
        setDraft(buildDefaultAsRepairQtyChartConfig());
    };

    const handleRevert = () => {
        setDraft(savedConfig);
    };

    const handleSave = async () => {
        if (!currentUserName) {
            showAppToast('로그인이 필요합니다.', 'error');
            return;
        }
        for (const group of draft.groups) {
            if (!String(group.title || '').trim()) {
                showAppToast('비어 있는 그룹 이름이 있습니다.', 'error');
                return;
            }
            const titles = group.categories.map((c) => String(c.title || '').trim());
            if (titles.some((t) => !t)) {
                showAppToast(`「${group.title}」에 비어 있는 하위 항목 이름이 있습니다.`, 'error');
                return;
            }
            const normalizedTitles = titles.map((t) => t.replace(/\s+/g, '').toLowerCase());
            if (new Set(normalizedTitles).size !== normalizedTitles.length) {
                showAppToast(`「${group.title}」에 같은 이름의 하위 항목이 있습니다.`, 'error');
                return;
            }
        }
        setBusy(true);
        try {
            const res = await fetch(`${getApiBaseUrl()}/as-repair-qty-chart-config`, {
                method: 'PUT',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: currentUserName,
                    config: normalizeAsRepairQtyChartConfig(draft),
                }),
            });
            const data = await readJsonResponse(res);
            onChartConfigSaved(data?.config || draft);
            showAppToast('그래프 설정이 저장되었습니다.', 'success');
        } catch (error) {
            showAppToast(error?.message || '저장에 실패했습니다.', 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="placeholder-page product-registration-settings-page qty-chart-settings-page">
            <div className="product-reg-settings-head">
                <div>
                    <h3>그래프 관리</h3>
                    <p className="product-reg-settings-help">
                        A/S 개인 관리 페이지 「수량 비중」 그래프의 세부사항 그룹과 하위 항목을 설정합니다.
                        연결 필드를 비워 두면 항목 이름과 같은 A/S 개인 관리 필드에 자동으로 연결됩니다.
                    </p>
                </div>
                <div className="product-reg-settings-head-actions">
                    {isDirty ? <span className="qty-chart-dirty">저장되지 않은 변경 사항</span> : null}
                    <button type="button" className="button button-cancel" onClick={handleRevert} disabled={busy || !isDirty}>
                        되돌리기
                    </button>
                    <button type="button" className="button" onClick={handleSave} disabled={busy || !isDirty}>
                        {busy ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>

            <div className="product-reg-settings-toolbar">
                <button type="button" className="button" onClick={addGroup} disabled={busy}>그룹 추가</button>
                <button type="button" className="button button-cancel" onClick={handleResetDefaults} disabled={busy}>기본값 복원</button>
            </div>

            <div className="qty-chart-group-list">
                {draft.groups.map((group, groupIndex) => {
                    const isOpen = openGroupIds.has(group.id);
                    const isBuiltin = BUILTIN_GROUP_IDS.has(group.id);
                    const groupPreview = preview[group.id];
                    return (
                        <section
                            key={group.id}
                            className={`qty-chart-group-card${group.visible ? '' : ' qty-chart-group-card--hidden'}`}
                        >
                            <div className="qty-chart-group-head">
                                <span className="product-reg-settings-order">{groupIndex + 1}</span>
                                <input
                                    type="color"
                                    className="qty-chart-color-input"
                                    value={group.color}
                                    onChange={(e) => updateGroup(groupIndex, { color: e.target.value })}
                                    disabled={busy}
                                    aria-label={`${group.title} 색상`}
                                />
                                <input
                                    type="text"
                                    className="field-input qty-chart-group-title-input"
                                    value={group.title}
                                    onChange={(e) => updateGroup(groupIndex, { title: e.target.value })}
                                    placeholder="그룹 이름"
                                    disabled={busy}
                                />
                                <div className="qty-chart-visible-toggle">
                                    <AppleSwitchCheckbox
                                        checked={group.visible}
                                        onChange={(e) => updateGroup(groupIndex, { visible: e.target.checked })}
                                        disabled={busy}
                                        aria-label={`${group.title} 그래프에 표시`}
                                    />
                                    <span>{group.visible ? '표시' : '숨김'}</span>
                                </div>
                                <div className="product-reg-settings-actions qty-chart-group-actions">
                                    <button type="button" className="button button-cancel model-edit-button" onClick={() => updateGroups((g) => moveItem(g, groupIndex, -1))} disabled={busy || groupIndex === 0}>▲</button>
                                    <button type="button" className="button button-cancel model-edit-button" onClick={() => updateGroups((g) => moveItem(g, groupIndex, 1))} disabled={busy || groupIndex === draft.groups.length - 1}>▼</button>
                                    {!isBuiltin ? (
                                        <button type="button" className="button button-cancel model-edit-button" onClick={() => removeGroup(groupIndex)} disabled={busy}>삭제</button>
                                    ) : null}
                                    <button
                                        type="button"
                                        className="button model-edit-button"
                                        onClick={() => toggleGroupOpen(group.id)}
                                        aria-expanded={isOpen}
                                    >
                                        {isOpen ? '접기 ▲' : `하위 ${group.categories.length}개 ▼`}
                                    </button>
                                </div>
                            </div>

                            {isOpen ? (
                                <div className="qty-chart-group-body">
                                    <div className="qty-chart-parent-row">
                                        <span className="qty-chart-parent-label">합계 필드</span>
                                        <div className="qty-chart-parent-picker">
                                            <FieldKeyPicker
                                                fieldKeys={group.fieldKeys}
                                                fields={qtyFields}
                                                onChange={(fieldKeys) => updateGroup(groupIndex, { fieldKeys })}
                                                disabled={busy}
                                                placeholder={group.fieldKeys.length > 0 ? '필드 추가…' : '자동 (필드 지정하려면 선택)'}
                                            />
                                            <AutoMatchHint
                                                explicit={group.fieldKeys.length > 0}
                                                matchedFields={groupPreview?.parentFields}
                                                emptyText="합계 필드 없음 — 하위 항목 수량을 더해 합계로 표시합니다."
                                            />
                                        </div>
                                    </div>

                                    <div className="product-reg-settings-table-wrap">
                                        <table className="product-reg-settings-table">
                                            <thead>
                                                <tr>
                                                    <th>순서</th>
                                                    <th>하위 항목 이름</th>
                                                    <th>연결 필드</th>
                                                    <th>관리</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.categories.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={4} className="qty-chart-empty">하위 항목이 없습니다.</td>
                                                    </tr>
                                                ) : null}
                                                {group.categories.map((cat, catIndex) => {
                                                    const catPreview = groupPreview?.categories?.[catIndex];
                                                    return (
                                                        <tr key={`${group.id}-${catIndex}`}>
                                                            <td className="product-reg-settings-order">{catIndex + 1}</td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="field-input"
                                                                    value={cat.title}
                                                                    onChange={(e) => updateCategory(groupIndex, catIndex, { title: e.target.value })}
                                                                    placeholder="그래프에 표시할 이름"
                                                                    disabled={busy}
                                                                />
                                                            </td>
                                                            <td className="qty-chart-field-cell">
                                                                <FieldKeyPicker
                                                                    fieldKeys={cat.fieldKeys}
                                                                    fields={qtyFields}
                                                                    onChange={(fieldKeys) => updateCategory(groupIndex, catIndex, { fieldKeys })}
                                                                    disabled={busy}
                                                                    placeholder={cat.fieldKeys.length > 0 ? '필드 추가…' : '자동 (필드 지정하려면 선택)'}
                                                                />
                                                                <AutoMatchHint
                                                                    explicit={cat.fieldKeys.length > 0}
                                                                    matchedFields={catPreview?.matchedFields}
                                                                    emptyText="연결된 필드 없음 — 그래프에 「항목 미설정」으로 표시됩니다."
                                                                />
                                                            </td>
                                                            <td className="product-reg-settings-actions">
                                                                <button type="button" className="button button-cancel model-edit-button" onClick={() => moveCategory(groupIndex, catIndex, -1)} disabled={busy || catIndex === 0}>▲</button>
                                                                <button type="button" className="button button-cancel model-edit-button" onClick={() => moveCategory(groupIndex, catIndex, 1)} disabled={busy || catIndex === group.categories.length - 1}>▼</button>
                                                                <button type="button" className="button button-cancel model-edit-button" onClick={() => removeCategory(groupIndex, catIndex)} disabled={busy}>삭제</button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button type="button" className="button" onClick={() => addCategory(groupIndex)} disabled={busy}>
                                        하위 항목 추가
                                    </button>
                                </div>
                            ) : null}
                        </section>
                    );
                })}
            </div>

            <div className="product-reg-settings-fixed">
                <h4>안내</h4>
                <ul>
                    <li>합계 필드: 그룹 막대의 수량입니다. 비워 두면 기본 그룹은 이름(반품·유상수리 등)으로 자동 탐색하고, 새 그룹은 하위 항목 합계를 사용합니다.</li>
                    <li>연결 필드: 하위 항목 수량을 가져올 A/S 개인 관리 필드입니다. 여러 개를 지정하면 합산합니다.</li>
                    <li>새 필드는 「제품등록 설정 → A/S 개인 관리」에서 수량 입력·자동 입력 항목으로 먼저 추가해야 선택할 수 있습니다.</li>
                    <li>기본 그룹(수량부족·반품·유상·무상)은 삭제 대신 「숨김」으로 그래프에서 감출 수 있습니다.</li>
                </ul>
            </div>
        </div>
    );
};

export default AsRepairQtyChartSettingsPage;
